import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ChevronDown, GraduationCap, User, X } from "lucide-react";
import PaginatorCst from "@components/PaginatorCst";
import useSWR from "swr";
import dayjs from "dayjs";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { EnrolledUserItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";

interface EnrollmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  objectId: number;
  objectTitle?: string;
  user: EnrolledUserItem | null;
}

const PAGE_SIZE = 20;

const NO_EXPIRATION_SENTINEL = "9999-12-31";

const formatDate = (value?: string | null): string =>
  value && !String(value).startsWith(NO_EXPIRATION_SENTINEL)
    ? dayjs(value).format("DD/MM/YYYY")
    : "-";

const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          padding: "12px 16px",
          borderRadius: "8px",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          fontSize: "14px",
          fontWeight: 600,
          color: theme.palette.text.primary,
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

const EnrollmentHistoryModal: React.FC<EnrollmentHistoryModalProps> = ({
  open,
  onClose,
  objectId,
  objectTitle,
  user,
}) => {
  const { t } = useTranslation("users-and-groups");
  const { t: tInput } = useTranslation("input");
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  useEffect(() => {
    if (open) {
      setPage(1);
      setExpandedIndex(0);
    }
  }, [open, user?.userId]);

  const url =
    open && user?.userId
      ? `${ServicesURL.getEnrollHistory}?objectId=${objectId}&userId=${
          user.userId
        }&sortEventsByCreationTime=DESC&pageIndex=${page - 1}&pageSize=${pageSize}`
      : null;

  const { data, isLoading } = useSWR(url, (u: string) =>
    fetcher<any>(u, MethodType.GET),
  );

  const events: any[] = (data as any)?.eventsResponseList ?? [];
  const totalItems: number = (data as any)?.metadata?.totalElements ?? 0;
  const totalPages: number =
    (data as any)?.metadata?.totalPages ??
    Math.max(Math.ceil(totalItems / pageSize), 1);

  const optionLabel = (group: string, value?: string | null): string => {
    if (value === undefined || value === null || value === "") return "-";
    const key = `options.${group}.${String(value).toLowerCase()}`;
    return String(tInput(key, { defaultValue: String(value) }));
  };

  const fullName = user
    ? [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim()
    : "";

  const fieldLabel = (key: string): string =>
    String(t(`history-modal.fields.${key}`));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                color: theme.palette.text.primary,
                fontSize: "14px",
              }}
            >
              <User size={16} />
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {fullName || "-"}
              </Typography>
              {objectTitle && (
                <>
                  <Typography
                    sx={{ color: theme.palette.divider, fontSize: "14px" }}
                  >
                    |
                  </Typography>
                  <GraduationCap size={16} />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {objectTitle}
                  </Typography>
                </>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("history-modal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : events.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
              color: theme.palette.text.primary,
              fontSize: "13px",
            }}
          >
            {t("history-modal.empty")}
          </Box>
        ) : (
          events.map((event, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <Box key={index}>
                <Divider sx={{ opacity: 0.6 }} />
                <Box
                  role="button"
                  onClick={() =>
                    setExpandedIndex((prev) => (prev === index ? null : index))
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    padding: "14px 4px",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("history-modal.updated-on")}{" "}
                    <b>{formatDate(event.updateTime)}</b>
                    {event.sessionUserId != null &&
                      ` (${t("history-modal.operator-id")}: ${
                        event.sessionUserId
                      })`}
                  </Typography>
                  <ChevronDown
                    size={18}
                    style={{
                      transition: "transform .15s",
                      transform: isExpanded ? "rotate(180deg)" : "none",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Collapse in={isExpanded} unmountOnExit>
                  <Grid container spacing={2} sx={{ pb: 2.5, pt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("method")}
                        value={optionLabel("event_type", event.eventType)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("group-id")}
                        value={
                          event.massiveGroupId != null
                            ? String(event.massiveGroupId)
                            : "-"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("channel")}
                        value={optionLabel("channel_enroll", event.channel)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("enroll-type")}
                        value={formatEnrollTypeLabel(event.enrollType) || "-"}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("creation-date")}
                        value={formatDate(event.creationTime)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("expiration-date")}
                        value={formatDate(event.expirationDate)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("update-date")}
                        value={formatDate(event.updateTime)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("completion-date")}
                        value={formatDate(event.completionTime)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("event-status")}
                        value={optionLabel("event_status", event.eventStatus)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReadOnlyField
                        label={fieldLabel("expiration-type")}
                        value={optionLabel(
                          "hard-soft-label",
                          event.expirationTypology,
                        )}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            );
          })
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentHistoryModal;
